import React, { useState, useEffect } from "react";
import { OI_URL } from "../../config/config";
import { Modal, Button, Form, Input, Select, Alert } from "antd";
import axios from "axios";
import Swal from "sweetalert2";
import Cookies from "js-cookie";

const { Option } = Select;

const AddBinCaidModal = (props) => {
    const { 
        t, 
        fetchBinCaids, 
        setIsAddBinCaidModalVisible, 
        isAddBinCaidModalVisible, 
        descriptorId = null, // Accept descriptorId as a prop with default value null
        merchantList = []
    } = props;
    
    const [formData, setFormData] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [form] = Form.useForm();
    
    // Initialize form data including descriptorId if provided
    const initFormData = {
        bin: "",
        caid: "",
        mcc: "",
        status: "",
        referenceNumber: "",
        descriptorId: descriptorId // Include descriptorId in the form data
    };

    const layout = {
        labelCol: { span: 4 },
        wrapperCol: { span: 18 },
    };

    // Re-initialize form when descriptorId changes
    useEffect(() => {
        formValInit();
    }, [descriptorId]); // eslint-disable-line react-hooks/exhaustive-deps

    const formValInit = () => {
        const initialData = {
            ...initFormData,
            descriptorId: descriptorId // Ensure descriptorId is set
        };
        setFormData(initialData);
    };

    const handleOk = () => {
        // Validate form before proceeding
        form.validateFields()
            .then(() => {
                onFinish();
                setIsSubmitting(true);
            })
            .catch((info) => {
                console.log('Validate Failed:', info);
            });
    };

    const formValChange = (chgV, allV) => {
        // Preserve descriptorId when form values change
        setFormData({ ...allV, descriptorId: descriptorId });
    };

    const onFinish = async () => {
        const apiUrl = OI_URL + `/api/v1/txshield/bincaid`;
        
        try {
            // Include descriptorId in the payload if it exists
            const payload = {
                ...formData,
                descriptorId: descriptorId
            };
            
            const { data } = await axios.post(apiUrl, payload, { 
                headers: { Authorization: "Bearer " + Cookies.get("txfind_token") } 
            });
            
            if (data.status === "ok") {
                Swal.fire({
                    icon: "success",
                    title: t("contents.oi.bincaid_added"),
                    showConfirmButton: false,
                    timer: 2000,
                    onClose: () => {
                        // Reset submitting state
                        setIsSubmitting(false);
                        
                        // Refresh data
                        if (fetchBinCaids && typeof fetchBinCaids === 'function') {
                            fetchBinCaids();
                        }
                        
                        // Reset form
                        form.resetFields();
                        
                        // Close modal
                        handleCancel();
                    },
                });
            } else {
                Swal.fire({
                    icon: "error",
                    title: t("login.dialogs.catch"),
                    text: data.msg ? data.msg : "",
                    showConfirmButton: true,
                });
                setIsSubmitting(false);
            }
        } catch (error) {
            console.error("Error adding BIN/CAID:", error);
            Swal.fire({
                icon: "error",
                title: t("login.dialogs.catch"),
                text: error.response ? error.response.data.msg : "",
                showConfirmButton: true,
            });
            setIsSubmitting(false);
        }
    };

    const isOkButtonProps = () => !formData || !formData.bin || !formData.caid || !formData.mcc || !formData.status;

    const handleCancel = () => {
        setIsAddBinCaidModalVisible(false);
        setIsSubmitting(false); // Ensure submitting state is reset
        form.resetFields(); // Reset form
    };

    return (
        <>
            <Modal
                title={t("contents.oi.new_bincaid")}
                width={600}
                visible={isAddBinCaidModalVisible}
                onOk={handleOk}
                onCancel={handleCancel}
                destroyOnClose={true} // Destroy modal when closed to prevent state issues
                footer={[
                    <Button key="back" onClick={() => handleCancel()}>
                        {t("contents.chargebacks.form.cancel")}
                    </Button>,
                    <Button 
                        key="submit" 
                        type="primary" 
                        loading={isSubmitting} 
                        disabled={isOkButtonProps()} 
                        onClick={() => handleOk()}
                    >
                        {isSubmitting ? t("contents.chargebacks.form.submitting") : t("contents.chargebacks.form.submit")}
                    </Button>,
                ]}
            >
                <Form 
                    form={form} 
                    {...layout} 
                    name="new_bincaid" 
                    onValuesChange={formValChange}
                    preserve={false} // Don't preserve form values between renders
                >
                    <Alert
                        style={{ marginBottom: 25 }}
                        message={t("contents.oi.form.addbincaidwarning")}
                        type="warning"
                        showIcon
                    />
                    <Form.Item name="bin" label={t("contents.oi.form.bin")} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="caid" label={t("contents.oi.form.caid")} rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="mcc" label="MCC" rules={[{ required: true }]}>
                        <Input />
                    </Form.Item>
                    <Form.Item name="merchantId" label={t("contents.oi.form.merchant")} rules={[{ required: true }]}>
                        <Select placeholder={t("contents.merchants.form.please_select")}>
                            {merchantList && merchantList.map((item) => (
                                <Option key={item.merchantId} value={item.merchantId}>
                                    {item.name}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="status" label={t("contents.oi.form.status")} rules={[{ required: true }]}>
                        <Select placeholder={t("contents.merchants.form.please_select")}>
                            <Option value="REQUESTED">REQUESTED</Option>
                            <Option value="PENDING">PENDING</Option>
                            <Option value="ENROLLED">ENROLLED</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="referenceNumber" label={t("contents.oi.form.referenceNumber")}>
                        <Input />
                    </Form.Item>
                    
                    {/* Hidden field for descriptor ID */}
                    {descriptorId && (
                        <Form.Item name="descriptorId" hidden={true}>
                            <Input />
                        </Form.Item>
                    )}
                </Form>
            </Modal>
        </>
    );
};

export default AddBinCaidModal;